import { set } from 'lodash-es';
import type { Recordable } from '@mts-fe/types';
import { isArray } from '..';

/** 可用多语言Code */
export const LOCALE = {
  zh_CN: 'zh-CN',
  zh_HK: 'zh-HK',
  en_US: 'en-US',
  km_KH: 'km-KH',
  id_ID: 'id-ID',
  vi_VN: 'vi-VN',
  ar_EG: 'ar-EG',
  ja_JP: 'ja-JP',
  ms_MY: 'ms-MY',
  th_TH: 'th-TH',
  lo_LA: 'lo-LA',
  ta_IN: 'ta-IN',
} as const;

export type LocaleType = (typeof LOCALE)[keyof typeof LOCALE];

export const LOCALE_LIST = [
  {
    label: '简体中文',
    value: LOCALE.zh_CN,
    langKey: 'c.25090340',
    fieldKey: 'zhCn',
  },
  {
    label: '繁体中文',
    value: LOCALE.zh_HK,
    langKey: 'c.25090341',
    fieldKey: 'zhHk',
  },
  {
    label: 'English',
    value: LOCALE.en_US,
    langKey: 'c.25090342',
    fieldKey: 'en',
  },
  {
    label: 'កម្ពុជា។',
    value: LOCALE.km_KH,
    langKey: 'c.25090343',
    fieldKey: 'kh',
  },
  {
    label: 'Bahasa Indonesia',
    value: LOCALE.id_ID,
    langKey: 'c.25090348',
    fieldKey: 'yid',
  },
  {
    label: 'Tiếng Việt',
    value: LOCALE.vi_VN,
    langKey: 'c.25090344',
    fieldKey: 'vi',
  },
  {
    label: 'اللغة العربية',
    value: LOCALE.ar_EG,
    langKey: 'c.26030439',
    fieldKey: 'ar',
  },
  {
    label: '日本語',
    value: LOCALE.ja_JP,
    langKey: 'c.26060332',
    fieldKey: 'ja',
  },
  {
    label: '马来语',
    value: LOCALE.ms_MY,
    langKey: 'c.25090345',
    fieldKey: 'ms',
  },
  {
    label: '泰语',
    value: LOCALE.th_TH,
    langKey: 'c.25090346',
    fieldKey: 'th',
  },
  {
    label: '老挝语',
    value: LOCALE.lo_LA,
    langKey: 'c.25090347',
    fieldKey: 'lao',
  },
  {
    label: '泰米尔语',
    value: LOCALE.ta_IN,
    langKey: 'c.25090349',
    fieldKey: 'ta',
  },
];

export function setHtmlPageLang(locale: LocaleType) {
  document.documentElement?.setAttribute('lang', locale);
}

export function genLocaleMessage(langs: Record<string, Recordable>, prefix = 'lang') {
  const obj: Recordable = {};

  Object.keys(langs).forEach((key) => {
    const langFileModule = langs[key].default;
    let fileName = key.replace(`./${prefix}/`, '').replace(/^\.\//, '');
    const lastIndex = fileName.lastIndexOf('.');
    fileName = fileName.substring(0, lastIndex);
    const keyList = fileName.split('/');
    const moduleName = keyList.shift();
    const objKey = keyList.join('.');

    if (moduleName) {
      if (objKey) {
        set(obj, moduleName, obj[moduleName] || {});
        set(obj[moduleName], objKey, langFileModule);
      } else {
        set(obj, moduleName, langFileModule || {});
      }
    }
  });
  return obj;
}

/** 内置的多语言模块Key */
export const BUILTIN_LOCALE_KEYS = ['antdLocale', 'common', 'component', 'sys'];

/** 扁平键名对象转成嵌套键名对象 */
export function expandLocaleObject<T = any>(flatObj: Recordable<T>) {
  const result: Recordable = {};

  for (const [key, value] of Object.entries(flatObj)) {
    if (typeof key !== 'string' || !key.includes('.')) {
      // 非字符串键或不含分隔符的键，直接保留
      result[key] = value;
      continue;
    }

    const keys = key.split('.');
    let currentLevel = result;
    keys.forEach((k, index) => {
      if (index === keys.length - 1) {
        currentLevel[k] = value;
      } else {
        if (currentLevel[k] && typeof currentLevel[k] !== 'object') {
          throw new Error(`Conflict at key '${k}': already exists as a non-object`);
        }
        currentLevel[k] = currentLevel[k] || {};
        currentLevel = currentLevel[k];
      }
    });
  }
  return result;
}

/** 嵌套键名对象转成扁平键名对象 */
export function collapseLocaleObject<T = any>(nestedObj: Recordable) {
  const result: Recordable<T> = {};

  function traverse(obj: Recordable, currentPath: string = '') {
    for (const [key, value] of Object.entries(obj)) {
      const newPath = currentPath ? `${currentPath}.${key}` : key;

      if (value && typeof value === 'object' && !isArray(value)) {
        // 如果是对象（非数组），继续递归遍历
        traverse(value, newPath);
      } else {
        result[newPath] = value as T;
      }
    }
  }

  traverse(nestedObj);
  return result;
}
